import React, { useState, useMemo, useRef, useEffect } from "react";
import "./SearchOrAddDropdown.css";
import { ChevronDown, ChevronUp } from "react-feather";

export default function SearchOrAddDropdown({
  options = [],
  value = [],
  onChange = () => {},
  placeholder = "Select…",
  mode = "multiple",
  showCount = true,
  noEditPlaceHolder = false,

  // ✅ New props (all default to preserve existing behavior)
  searchMode = "search-or-add",        // "search-or-add" | "search-only"
  returnType = "value",                 // "value" | "object" (select entire row)
  labelKey = "label",
  valueKey = "value",
}) {
  const [query, setQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [allOptions, setAllOptions] = useState(options);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setAllOptions(options);
  }, [options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

const getLabel = useMemo(() => {
  return (opt) => (opt?.[labelKey] ?? "").toString();
}, [labelKey]);

const getValue = useMemo(() => {
  return (opt) => opt?.[valueKey];
}, [valueKey]);

  

  const filteredOptions = useMemo(() => {
    const q = query.toLowerCase();
    return allOptions.filter((opt) => getLabel(opt).toLowerCase().includes(q));
  }, [query, allOptions, labelKey]);

  const multi = mode === "multiple";

  // Normalize current selected "values" for comparison
  // For returnType "object", we compare using valueKey under the hood
  const selectedValueKeys = useMemo(() => {
    if (multi) {
      if (returnType === "value") return new Set(value);
      // returnType === "object"
      return new Set((value || []).map((v) => v?.[valueKey]));
    } else {
      if (returnType === "value") return new Set([value].filter(Boolean));
      return new Set([value?.[valueKey]].filter(Boolean));
    }
  }, [value, multi, returnType, valueKey]);

  const isSelected = (opt) => selectedValueKeys.has(getValue(opt));

  const addToSelection = (item) => {
    if (multi) {
      if (returnType === "value") {
        onChange([...(value || []), getValue(item)]);
      } else {
        onChange([...(value || []), item]);
      }
    } else {
      onChange(returnType === "value" ? getValue(item) : item);
      setShowDropdown(false);
    }
  };

  const removeFromSelection = (item) => {
    if (multi) {
      if (returnType === "value") {
        onChange((value || []).filter((v) => v !== getValue(item)));
      } else {
        const toRemoveKey = getValue(item);
        onChange((value || []).filter((obj) => obj?.[valueKey] !== toRemoveKey));
      }
    } else {
      // Single-select: clearing behaves like re-selecting something else; noop here.
    }
  };

  const handleToggle = (item) => {
    if (isSelected(item)) removeFromSelection(item);
    else addToSelection(item);
  };

  const canAdd = searchMode === "search-or-add";

  const handleAddNew = () => {
    const trimmed = query.trim();
    if (!trimmed || !canAdd) return;

    const newVal = trimmed.toLowerCase().replace(/\s+/g, "_");
    // Build a new object respecting labelKey/valueKey, preserving other keys minimalistically
    const newItem = { [labelKey]: trimmed, [valueKey]: newVal };

    setAllOptions((prev) => [...prev, newItem]);
    addToSelection(newItem);
    setQuery("");
    if (!multi) setShowDropdown(false);
  };

  // Build display text
  const getDisplayLabel = () => {
    if (noEditPlaceHolder) return placeholder;

    // Resolve selected items/labels
    if (multi) {
      const count =
        returnType === "value"
          ? (value || []).length
          : (value || []).length;

      if (count === 0) return placeholder;
      if (showCount) return `${count} selected`;

      // show labels instead of count
      const labels =
        returnType === "value"
          ? allOptions
              .filter((opt) => (value || []).includes(getValue(opt)))
              .map(getLabel)
          : (value || []).map((v) => v?.[labelKey]).filter(Boolean);

      return labels.join(", ") || placeholder;
    } else {
      if (!value || (returnType === "value" && !value)) return placeholder;

      if (returnType === "value") {
        const matched = allOptions.find((o) => getValue(o) === value);
        return matched ? getLabel(matched) : placeholder;
      } else {
        return value?.[labelKey] ?? placeholder;
      }
    }
  };

  const showAddRow =
    canAdd &&
    query.length > 0 &&
    !allOptions.some(
      (o) => getLabel(o).toLowerCase() === query.trim().toLowerCase()
    );

  return (
    <div className="sadd-wrapper" ref={dropdownRef}>
      <div
        className={`sadd-input-wrapper ${showDropdown ? "active" : ""}`}
        onClick={() => setShowDropdown((prev) => !prev)}
      >
        <div className="sadd-placeholder">{getDisplayLabel()}</div>
        <div className="sadd-arrow">
          {showDropdown ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </div>

      {showDropdown && (
        <div className="sadd-dropdown">
          <input
            type="text"
            className="sadd-dropdown-search"
            placeholder={canAdd ? "Search or add…" : "Search…"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          <div className="sadd-options-list">
            {filteredOptions.map((opt) => (
              <div
                key={getValue(opt)}
                className={`sadd-option ${isSelected(opt) ? "selected" : ""}`}
                onClick={() => handleToggle(opt)}
              >
                {multi && (
                  <span className="sadd-check">{isSelected(opt) ? "✓" : ""}</span>
                )}
                
               
                <span className="sadd-label">{getLabel(opt)}</span>

              </div>
            ))}

            {showAddRow && (
              <div className="sadd-option sadd-add-option" onClick={handleAddNew}>
                Add “{query}”
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
  